import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/api';
import * as XLSX from 'xlsx';
import { filterMrpWarehouseList } from '../utils/mrpWarehouseFilter';

const BOM_TYPES = ['Production', 'Template', 'Sales'];
const ISSUE_METHODS = ['Backflush', 'Manual'];
const INP = 'w-full border border-sap-border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-sap-blue';
const ITEM_PICKER_PAGE_SIZE = 250;
const ITEM_BULK_WARN = 200;
/** BOM components grid: max rows rendered at once (full line list stays in memory; DOM stays small). */
const COMPONENTS_TABLE_PAGE_SIZE = 50;

function SearchableSelect({ value, onChange, options, placeholder = 'Select…', className = '', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      if (ref.current && ref.current.contains(t)) return;
      if (menuRef.current && menuRef.current.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (disabled) return;
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 240;
      setPos({
        top: openUp ? rect.top : rect.bottom + 2,
        left: rect.left,
        width: rect.width,
        openUp,
      });
    }
    setOpen(!open);
    setQ('');
  };

  const filtered = options.filter(o =>
    !q || o.label.toLowerCase().includes(q.toLowerCase())
  );
  const selectedLabel = options.find(o => String(o.value) === String(value))?.label || '';

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button ref={btnRef} type="button" disabled={disabled}
        onClick={handleOpen}
        className={`${INP} text-left flex items-center justify-between gap-1 h-8 ${disabled ? 'bg-gray-50 text-sap-textLight cursor-default' : 'bg-white cursor-pointer'}`}>
        <span className={`truncate ${!selectedLabel ? 'text-sap-textLight' : ''}`}>
          {selectedLabel || placeholder}
        </span>
        <svg width="10" height="10" viewBox="0 0 10 10" className="flex-shrink-0 text-sap-textLight">
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
        </svg>
      </button>
      {open && ReactDOM.createPortal(
        <div ref={menuRef} style={{
          position: 'fixed',
          left: pos.left,
          width: Math.max(pos.width, 180),
          ...(pos.openUp
            ? { bottom: window.innerHeight - pos.top + 2 }
            : { top: pos.top }),
          zIndex: 9999,
        }}
          className="bg-white border border-sap-border rounded-lg shadow-lg max-h-56 flex flex-col">
          <div className="px-2 pt-2 pb-1 border-b border-sap-border/50">
            <input autoFocus value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search…"
              className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:ring-1 focus:ring-sap-blue/40" />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-sap-textLight text-center">No matches</div>
            ) : filtered.map(o => (
              <div key={o.value}
                onMouseDown={(e) => {
                  // Select before document mousedown closes the portal.
                  e.preventDefault();
                  onChange(o.value);
                  setOpen(false);
                  setQ('');
                }}
                className={`px-3 py-1.5 text-xs cursor-pointer hover:bg-sap-blue/10 ${String(o.value) === String(value) ? 'bg-sap-blue/15 font-semibold text-sap-blue' : 'text-sap-text'}`}>
                {o.label}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * Item picker: optional client `items` OR server `fetchPage` (never loads full catalog into memory).
 * Multiple mode: select all on page / all matches (server id list) + bulk add.
 */
function PaginatedItemPicker({
  value,
  valueLabel = '',
  onChange,
  onBulkAdd,
  items = [],
  fetchPage,
  fetchMatchingIds,
  placeholder = 'Select…',
  className = '',
  disabled = false,
  multiple = false,
  makeOnly = false,
  excludeItemIds = null,
}) {
  const serverMode = typeof fetchPage === 'function';
  const [open, setOpen] = useState(false);
  const [qInput, setQInput] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, openUp: false });
  const [remoteItems, setRemoteItems] = useState([]);
  const [remoteTotal, setRemoteTotal] = useState(0);
  const [loadingRemote, setLoadingRemote] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const exclude = useMemo(() => {
    if (!excludeItemIds) return new Set();
    return excludeItemIds instanceof Set ? excludeItemIds : new Set(excludeItemIds);
  }, [excludeItemIds]);

  const excludeArr = useMemo(() => [...exclude], [exclude]);

  const pool = useMemo(() => {
    if (serverMode) return [];
    return (items || []).filter((i) => i && i.item_id != null && !exclude.has(Number(i.item_id)));
  }, [items, exclude, serverMode]);

  const filtered = useMemo(() => {
    if (serverMode) return [];
    const qq = qInput.trim().toLowerCase();
    if (!qq) return pool;
    return pool.filter((i) => {
      const no = String(i.item_no || '').toLowerCase();
      const desc = String(i.description || '').toLowerCase();
      return no.includes(qq) || desc.includes(qq);
    });
  }, [pool, qInput, serverMode]);

  useEffect(() => {
    if (serverMode) setPage(0);
  }, [qDebounced, serverMode]);

  useEffect(() => {
    if (!serverMode) setPage(0);
  }, [qInput, serverMode]);

  const totalPages = serverMode
    ? Math.max(1, Math.ceil(remoteTotal / ITEM_PICKER_PAGE_SIZE))
    : Math.max(1, Math.ceil(filtered.length / ITEM_PICKER_PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages - 1);
  const pageSlice = serverMode
    ? remoteItems
    : filtered.slice(pageSafe * ITEM_PICKER_PAGE_SIZE, (pageSafe + 1) * ITEM_PICKER_PAGE_SIZE);

  const filteredCount = serverMode ? remoteTotal : filtered.length;

  useEffect(() => {
    if (!open || !serverMode || !fetchPage) return;
    let cancelled = false;
    (async () => {
      setLoadingRemote(true);
      try {
        const data = await fetchPage({
          page: pageSafe,
          q: qDebounced,
          makeOnly,
          excludeIds: excludeArr,
        });
        if (cancelled) return;
        setRemoteItems(data.items || []);
        setRemoteTotal(Number(data.total) || 0);
      } catch {
        if (!cancelled) {
          setRemoteItems([]);
          setRemoteTotal(0);
        }
      } finally {
        if (!cancelled) setLoadingRemote(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, serverMode, fetchPage, pageSafe, qDebounced, makeOnly, excludeArr]);

  const selectedLabel = useMemo(() => {
    if (serverMode && valueLabel) return valueLabel;
    const row = pool.find((i) => Number(i.item_id) === Number(value));
    return row?.item_no || '';
  }, [pool, value, valueLabel, serverMode]);

  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      if (ref.current && ref.current.contains(t)) return;
      if (menuRef.current && menuRef.current.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (disabled) return;
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 320;
      setPos({
        top: openUp ? rect.top : rect.bottom + 2,
        left: rect.left,
        width: rect.width,
        openUp,
      });
      setQInput('');
      setQDebounced('');
      setPage(0);
      setSelected(new Set());
      if (serverMode) {
        setRemoteItems([]);
        setRemoteTotal(0);
      }
    }
    setOpen(!open);
  };

  const toggleId = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const n = Number(id);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };

  const allOnPageSelected =
    multiple && pageSlice.length > 0 && pageSlice.every((i) => selected.has(Number(i.item_id)));
  const toggleSelectAllPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const ids = pageSlice.map((i) => Number(i.item_id));
      if (allOnPageSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const selectAllMatching = async () => {
    if (serverMode && typeof fetchMatchingIds === 'function') {
      setLoadingRemote(true);
      try {
        const ids = await fetchMatchingIds({ q: qDebounced, makeOnly, excludeIds: excludeArr });
        const n = ids.length;
        if (n === 0) return;
        if (n > ITEM_BULK_WARN) {
          const ok = window.confirm(
            `Select all ${n.toLocaleString()} items matching this search? This may add many BOM lines.`
          );
          if (!ok) return;
        }
        setSelected(new Set(ids.map(Number)));
      } finally {
        setLoadingRemote(false);
      }
      return;
    }
    const n = filtered.length;
    if (n === 0) return;
    if (n > ITEM_BULK_WARN) {
      const ok = window.confirm(
        `Select all ${n.toLocaleString()} items matching this search? This may add many BOM lines.`
      );
      if (!ok) return;
    }
    setSelected(new Set(filtered.map((i) => Number(i.item_id))));
  };

  const clearSelection = () => setSelected(new Set());

  const applyBulk = () => {
    if (!onBulkAdd || selected.size === 0) return;
    onBulkAdd([...selected]);
    setOpen(false);
    setSelected(new Set());
    setQInput('');
    setQDebounced('');
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        className={`${INP} text-left flex items-center justify-between gap-1 h-8 ${
          disabled ? 'bg-gray-50 text-sap-textLight cursor-default' : 'bg-white cursor-pointer'
        }`}
      >
        <span className={`truncate ${!selectedLabel && !multiple ? 'text-sap-textLight' : ''}`}>
          {multiple ? (open ? 'Pick items…' : placeholder) : selectedLabel || placeholder}
        </span>
        <svg width="10" height="10" viewBox="0 0 10 10" className="flex-shrink-0 text-sap-textLight">
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" />
        </svg>
      </button>
      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              left: pos.left,
              width: Math.max(pos.width, multiple ? 320 : 220),
              ...(pos.openUp ? { bottom: window.innerHeight - pos.top + 2 } : { top: pos.top }),
              zIndex: 9999,
            }}
            className="bg-white border border-sap-border rounded-lg shadow-lg max-h-[min(420px,70vh)] flex flex-col"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="px-2 pt-2 pb-1 border-b border-sap-border/50 space-y-1.5">
              <input
                autoFocus
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Search item no. or description…"
                className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:ring-1 focus:ring-sap-blue/40"
              />
              {multiple && (
                <div className="flex flex-wrap gap-1.5 items-center">
                  <button
                    type="button"
                    onClick={toggleSelectAllPage}
                    className="px-2 py-0.5 text-2xs font-semibold rounded border border-sap-border hover:bg-sap-blue/10 text-sap-text"
                  >
                    {allOnPageSelected ? 'Uncheck page' : 'Select all on page'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void selectAllMatching()}
                    disabled={filteredCount === 0 || loadingRemote}
                    className="px-2 py-0.5 text-2xs font-semibold rounded border border-sap-border hover:bg-sap-blue/10 text-sap-text disabled:opacity-40"
                  >
                    Select all matches ({filteredCount.toLocaleString()})
                  </button>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="px-2 py-0.5 text-2xs font-semibold rounded border border-sap-border/70 text-sap-textLight hover:bg-gray-50"
                  >
                    Clear checks
                  </button>
                  <span className="text-2xs text-sap-textLight ml-auto">
                    {selected.size > 0 ? `${selected.size} selected` : ''}
                    {loadingRemote ? ' · Loading…' : ''}
                  </span>
                </div>
              )}
            </div>
            <div className="overflow-y-auto flex-1 min-h-0">
              {loadingRemote && pageSlice.length === 0 ? (
                <div className="px-3 py-6 text-xs text-sap-textLight text-center">Loading…</div>
              ) : pageSlice.length === 0 ? (
                <div className="px-3 py-6 text-xs text-sap-textLight text-center">No matches</div>
              ) : (
                pageSlice.map((it) => {
                  const id = Number(it.item_id);
                  const checked = multiple && selected.has(id);
                  const isSingleSel = !multiple && String(value) === String(id);
                  return (
                    <div
                      key={id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (multiple) toggleId(id);
                        else if (onChange) {
                          onChange(id);
                          setOpen(false);
                          setQInput('');
                          setQDebounced('');
                        }
                      }}
                      className={`px-2 py-1.5 text-xs cursor-pointer flex items-start gap-2 border-b border-sap-border/30 last:border-b-0 hover:bg-sap-blue/10 ${
                        isSingleSel ? 'bg-sap-blue/15 font-semibold text-sap-blue' : 'text-sap-text'
                      }`}
                    >
                      {multiple && (
                        <input
                          type="checkbox"
                          readOnly
                          checked={checked}
                          className="mt-0.5 rounded border-sap-border accent-sap-blue"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate">{it.item_no}</div>
                        <div className="text-2xs text-sap-textLight truncate">{it.description || '—'}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="px-2 py-1.5 border-t border-sap-border/50 flex flex-wrap items-center gap-2 justify-between bg-gray-50/80 rounded-b-lg">
              <div className="flex items-center gap-1 text-2xs text-sap-textLight">
                <button
                  type="button"
                  disabled={pageSafe <= 0 || loadingRemote}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="px-1.5 py-0.5 rounded border border-sap-border disabled:opacity-40 hover:bg-white"
                >
                  Prev
                </button>
                <span>
                  Page {pageSafe + 1} / {totalPages} ({filteredCount.toLocaleString()} items)
                </span>
                <button
                  type="button"
                  disabled={pageSafe >= totalPages - 1 || loadingRemote}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  className="px-1.5 py-0.5 rounded border border-sap-border disabled:opacity-40 hover:bg-white"
                >
                  Next
                </button>
              </div>
              {multiple && onBulkAdd && (
                <button
                  type="button"
                  disabled={selected.size === 0 || loadingRemote}
                  onClick={applyBulk}
                  className="px-3 py-1 text-2xs font-bold rounded-lg bg-sap-blue text-white disabled:opacity-50 hover:bg-sap-blueDark"
                >
                  Add {selected.size > 0 ? `${selected.size} ` : ''}component(s)
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* ───────────────────────────────────────────
   Main Component – list / detail switching
   ─────────────────────────────────────────── */
export default function BillOfMaterials() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState('list');
  const [boms, setBoms] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [pageInput, setPageInput] = useState('1');
  const [bomStack, setBomStack] = useState([]); // breadcrumb stack: [{bom_id, product_no}]

  const totalPages = Math.ceil(total / limit) || 1;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search.trim()) params.set('search', search.trim());
      const data = await api.get(`/bom?${params}`);
      if (data && Array.isArray(data.rows)) {
        setBoms(data.rows);
        setTotal(data.total || 0);
      } else if (Array.isArray(data)) {
        setBoms(data);
        setTotal(data.length);
      }
    } catch {
      setError('Failed to load Bill of Materials');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const submitPageJump = () => {
    const n = parseInt(String(pageInput).trim(), 10);
    if (!Number.isFinite(n)) {
      setPageInput(String(page));
      return;
    }
    const clamped = Math.min(Math.max(1, n), totalPages);
    setPage(clamped);
    setPageInput(String(clamped));
  };

  /* Auto-open BOM when navigated from Item Master golden arrow */
  useEffect(() => {
    const openBomId = location.state?.openBomId;
    if (openBomId) {
      setSelectedId(openBomId);
      setBomStack([]);
      setMode('detail');
    }
  }, [location.state]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); };
  const openDetail = (id) => { setSelectedId(id); setBomStack([]); setMode('detail'); setError(''); };
  const openNew = () => { setSelectedId(null); setBomStack([]); setMode('detail'); setError(''); };
  const backToList = () => { setMode('list'); setBomStack([]); load(); };

  /* Drill into a component's BOM */
  const drillIntoBom = (bomId, productNo) => {
    setBomStack(prev => [...prev, { bom_id: selectedId, product_no: prev.length === 0 ? 'root' : prev[prev.length-1]?.product_no }]);
    setSelectedId(bomId);
  };

  const navigateBreadcrumb = (idx) => {
    if (idx < 0) { backToList(); return; }
    const entry = bomStack[idx];
    setSelectedId(entry.bom_id);
    setBomStack(prev => prev.slice(0, idx));
  };

  const handleExportAllBomsList = async () => {
    try {
      setLoading(true);
      const data = await api.get('/bom?limit=5000');
      const rawRows = data && Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : []);
      const rows = rawRows.map((b, i) => ({
        '#': i + 1,
        'Product No.': b.product_no || '',
        'Product Description': b.product_desc || '',
        'BOM Type': b.bom_type || '',
        'Components Count': b.line_count || 0,
        'Status': b.status || '',
        'Created Date': b.created_at ? new Date(b.created_at).toLocaleDateString('en-IN') : '',
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, 'All_BOMs');
      const fileName = `Bill_of_Materials_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
      flash('BOM list exported to Excel');
    } catch (err) {
      setError('Failed to export BOM list: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'detail') {
    return <BOMDetail bomId={selectedId} onBack={backToList} flash={flash}
                      bomStack={bomStack} drillIntoBom={drillIntoBom}
                      navigateBreadcrumb={navigateBreadcrumb} />;
  }

  const fromRec = total === 0 ? 0 : (page - 1) * limit + 1;
  const toRec = Math.min(page * limit, total);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="page-title">Bill of Materials</h1>
          <p className="text-xs text-sap-textLight mt-0.5">
            Total {total.toLocaleString('en-IN')} BOMs in system
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input className="search-input w-64" placeholder="Search product no. or description…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <button type="button" onClick={handleExportAllBomsList} className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export List
          </button>
          <button type="button" onClick={() => navigate('/production/bom-import')} className="btn-secondary text-xs py-1.5 px-3">
            Import CSV
          </button>
          <button type="button" onClick={openNew} className="btn-primary">
            + New BOM
          </button>
        </div>
      </div>

      {success && <Banner type="success">{success}</Banner>}
      {error && <Banner type="error">{error}</Banner>}

      {loading ? (
        <div className="py-16 text-center text-xs text-sap-textLight">Loading…</div>
      ) : boms.length === 0 ? (
        <div className="card py-16 text-center text-sm text-sap-textLight">
          {search ? 'No BOMs match your search query.' : 'No Bill of Materials yet. Click "+ New BOM" to create one.'}
        </div>
      ) : (
        <>
          <div className="data-table-wrap overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  {['Product No.', 'Product Description', 'BOM Type', 'Components', 'Status', 'Created'].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {boms.map((b) => (
                  <tr key={b.bom_id} onClick={() => openDetail(b.bom_id)} className="table-row even:bg-gray-50/50 cursor-pointer hover:bg-sap-blue/5">
                    <td className="px-4 py-3 font-semibold text-sap-blue">{b.product_no}</td>
                    <td className="px-4 py-3">{b.product_desc}</td>
                    <td className="px-4 py-3">{b.bom_type}</td>
                    <td className="px-4 py-3 text-center font-semibold">{b.line_count}</td>
                    <td className="px-4 py-3">
                      {b.status === 'Active' ? (
                        <span className="badge-active">{b.status}</span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-500">{b.status}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sap-textLight">
                      {b.created_at ? new Date(b.created_at).toLocaleDateString('en-IN') : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination bar */}
          <div className="flex items-center justify-between flex-wrap gap-3 pt-2 text-xs text-sap-textLight border-t border-sap-border/40">
            <div>
              Showing <span className="font-semibold text-sap-text">{fromRec}</span> to <span className="font-semibold text-sap-text">{toRec}</span> of <span className="font-semibold text-sap-text">{total.toLocaleString('en-IN')}</span> BOMs
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span>Rows per page:</span>
                <select
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="border border-sap-border rounded-lg px-2 py-1 bg-white text-xs text-sap-text focus:outline-none"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-30 hover:bg-sap-blue/10 font-semibold"
                >
                  ‹
                </button>
                <span className="px-2">
                  Page <span className="font-semibold text-sap-text">{page}</span> of <span className="font-semibold text-sap-text">{totalPages}</span>
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  className="px-2.5 py-1 rounded-lg border border-sap-border disabled:opacity-30 hover:bg-sap-blue/10 font-semibold"
                >
                  ›
                </button>
              </div>

              <div className="flex items-center gap-1.5 pl-2 border-l border-sap-border/40">
                <span>Go to:</span>
                <input
                  type="text"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitPageJump(); }}
                  onBlur={submitPageJump}
                  className="w-12 border border-sap-border rounded-lg px-1.5 py-1 text-center bg-white text-xs text-sap-text focus:outline-none"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


/* ───────────────────────────────────────────
   BOM Detail – SAP B1 style
   ─────────────────────────────────────────── */
function BOMDetail({ bomId, onBack, flash, bomStack = [], drillIntoBom, navigateBreadcrumb }) {
  const isNew = !bomId;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    product_item_id: '', bom_type: 'Production', status: 'Active',
    quantity: '1', planned_avg_production_size: '1',
    warehouse_id: '', to_warehouse_id: '', price_list: '', distr_rule: '', project: '',
  });
  const [productNo, setProductNo] = useState('');
  const [productDesc, setProductDesc] = useState('');
  const [productUom, setProductUom] = useState('');
  const [productPrice, setProductPrice] = useState(0);
  const [lines, setLines] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState('contents');
  const [componentBomMap, setComponentBomMap] = useState({}); // item_id → bom_id (for drill-down)
  const [compTablePage, setCompTablePage] = useState(0);

  const buildItemsPageQuery = useCallback((params) => {
    const { page, q, makeOnly, excludeIds } = params;
    const qs = new URLSearchParams({
      page: String(page),
      limit: String(ITEM_PICKER_PAGE_SIZE),
      make_only: makeOnly ? '1' : '0',
    });
    if (q) qs.set('q', q);
    if (excludeIds?.length) qs.set('exclude_ids', excludeIds.map(String).join(','));
    return `/mrp/items-page?${qs.toString()}`;
  }, []);

  const fetchItemsPage = useCallback(
    async (opts) => api.get(buildItemsPageQuery(opts)),
    [buildItemsPageQuery]
  );

  const fetchMatchingItemIds = useCallback(async ({ q, makeOnly, excludeIds }) => {
    const qs = new URLSearchParams({ ids_only: '1', make_only: makeOnly ? '1' : '0' });
    if (q) qs.set('q', q);
    if (excludeIds?.length) qs.set('exclude_ids', excludeIds.map(String).join(','));
    const data = await api.get(`/mrp/items-page?${qs.toString()}`);
    return data.ids || [];
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [whData, projData] = await Promise.all([
          api.get('/mrp/warehouses'),
          api.get('/projects').catch(() => []),
        ]);
        setWarehouses(filterMrpWarehouseList(whData || []));
        setProjects(Array.isArray(projData) ? projData : []);

        if (!isNew) {
          setLoading(true);
          const data = await api.get(`/bom/${bomId}/detail`);
          setForm({
            product_item_id: data.product_item_id,
            bom_type: data.bom_type,
            status: data.status,
            quantity: String(data.quantity || 1),
            planned_avg_production_size: String(data.planned_avg_production_size || 1),
            warehouse_id: data.warehouse_id || '',
            to_warehouse_id: data.to_warehouse_id || '',
            price_list: data.price_list || '',
            distr_rule: data.distr_rule || '',
            project: data.project || '',
          });
          setProductNo(data.product_no || '');
          setProductDesc(data.product_desc || '');
          setProductUom(data.product_uom || '');
          setProductPrice(Number(data.product_price) || 0);
          setLines((data.lines || []).map(l => ({
            component_item_id: l.component_item_id,
            item_no: l.item_no,
            component_desc: l.component_desc,
            comp_type: l.comp_type || 'Goods',
            quantity: Number(l.quantity),
            uom: l.uom_name || l.uom || 'PCS',
            warehouse_id: l.warehouse_id,
            wh_code: l.wh_code || '',
            issue_method: l.issue_method || 'Backflush',
            unit_price: Number(l.unit_price) || 0,
            comments: l.comments || '',
            price_list: l.price_list || '',
            wip_account: l.wip_account || '',
            route_sequence: l.route_sequence || '',
            project_code: l.project_code || '',
            project_name: l.project_name || '',
          })));
          setAttachments(data.attachments || []);
        } else {
          setAttachments([]);
        }
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, [bomId, isNew]);

  /* Check which component items have their own BOMs (for drill-down arrows) */
  useEffect(() => {
    if (lines.length === 0) return;
    (async () => {
      try {
        const allBoms = await api.get('/bom');
        const map = {};
        (allBoms || []).forEach(b => { map[b.product_item_id] = b.bom_id; });
        setComponentBomMap(map);
      } catch { /* ignore */ }
    })();
  }, [lines]);

  const compTotalPages = Math.max(1, Math.ceil(lines.length / COMPONENTS_TABLE_PAGE_SIZE));
  const compPageSafe = Math.min(compTablePage, compTotalPages - 1);
  const compRowStart = compPageSafe * COMPONENTS_TABLE_PAGE_SIZE;
  const linesOnPage = lines.slice(compRowStart, compRowStart + COMPONENTS_TABLE_PAGE_SIZE);

  useEffect(() => {
    const maxP = Math.max(0, compTotalPages - 1);
    if (compTablePage > maxP) setCompTablePage(maxP);
  }, [lines.length, compTotalPages, compTablePage]);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setDirty(true);
    if (k === 'warehouse_id' && v) {
      const wh = warehouses.find(w => w.warehouse_id === Number(v));
      setLines(prev => prev.map(l => ({ ...l, warehouse_id: Number(v), wh_code: wh?.wh_code || '' })));
    }
    if (k === 'project') {
      const proj = projects.find(p => p.project_code === v);
      setLines(prev => prev.map(l => ({ ...l, project_code: v || '', project_name: proj?.project_name || '' })));
    }
  };

  const selectProduct = async (itemId) => {
    try {
      const rows = await api.post('/mrp/items-resolve', { ids: [Number(itemId)] });
      const item = rows[0];
      if (!item) return;
      setForm((f) => ({ ...f, product_item_id: item.item_id }));
      setProductNo(item.item_no);
      setProductDesc(item.description || '');
      setProductUom(item.uom_name || '');
      setProductPrice(Number(item.unit_price) || 0);
      setDirty(true);
    } catch (e) {
      setError(e.message || 'Failed to load item');
    }
  };

  const addLine = () => {
    const wh = form.warehouse_id ? warehouses.find(w => w.warehouse_id === Number(form.warehouse_id)) : null;
    const proj = form.project ? projects.find(p => p.project_code === form.project) : null;
    setLines(prev => [...prev, {
      component_item_id: '', item_no: '', component_desc: '', comp_type: '',
      quantity: 1, uom: 'PCS',
      warehouse_id: form.warehouse_id || '', wh_code: wh?.wh_code || '',
      issue_method: 'Backflush', unit_price: 0, comments: '',
      price_list: '', wip_account: '', route_sequence: '',
      project_code: form.project || '', project_name: proj?.project_name || '',
    }]);
    setDirty(true);
  };

  /** Add one or many component lines at idx (replaces the empty row). Loads row data from API (no full item list in memory). */
  const bulkAddComponentsAt = async (idx, itemIds) => {
    if (!itemIds || itemIds.length === 0) return;
    const uniq = [...new Set(itemIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
    if (uniq.length === 0) return;
    try {
      const resolved = await api.post('/mrp/items-resolve', { ids: uniq });
      const byId = Object.fromEntries(resolved.map((r) => [Number(r.item_id), r]));
      const wh = form.warehouse_id ? warehouses.find((w) => w.warehouse_id === Number(form.warehouse_id)) : null;
      const proj = form.project ? projects.find((p) => p.project_code === form.project) : null;
      const lineFromItemId = (iid) => {
        const item = byId[Number(iid)];
        return {
          component_item_id: Number(iid),
          item_no: item?.item_no || '',
          component_desc: item?.description || '',
          comp_type: item?.item_type || 'Goods',
          quantity: 1,
          uom: item?.uom_name || 'PCS',
          warehouse_id: form.warehouse_id || '',
          wh_code: wh?.wh_code || '',
          issue_method: 'Backflush',
          unit_price: Number(item?.unit_price) || 0,
          comments: '',
          price_list: '',
          wip_account: '',
          route_sequence: '',
          project_code: form.project || '',
          project_name: proj?.project_name || '',
        };
      };
      setLines((prev) => {
        const next = [...prev];
        const newRows = uniq.map(lineFromItemId);
        next.splice(idx, 1, ...newRows);
        return next;
      });
      setDirty(true);
    } catch (e) {
      setError(e.message || 'Failed to resolve items');
    }
  };

  const updateLine = (idx, field, value) => {
    setLines(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'warehouse_id') {
        const wh = warehouses.find(w => w.warehouse_id === Number(value));
        next[idx].wh_code = wh?.wh_code || '';
      }
      return next;
    });
    setDirty(true);
  };

  const removeLine = (idx) => {
    setLines(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  /* Production Std Cost = sum of line totals */
  const prodQty = parseFloat(form.quantity) || 1;
  const prodStdCost = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);

  const handleSave = async () => {
    if (!form.product_item_id) return setError('Product No. is required');
    if (lines.length === 0) return setError('At least one component line is required');
    const invalidLines = lines.filter(l => !l.component_item_id);
    if (invalidLines.length) return setError('All lines must have a component item selected');

    setSaving(true); setError('');
    try {
      const payload = {
        product_item_id: form.product_item_id,
        bom_type: form.bom_type,
        status: form.status,
        quantity: parseFloat(form.quantity) || 1,
        planned_avg_production_size: parseFloat(form.planned_avg_production_size) || 1,
        warehouse_id: form.warehouse_id || null,
        to_warehouse_id: form.to_warehouse_id || null,
        price_list: form.price_list || null,
        distr_rule: form.distr_rule || null,
        project: form.project || null,
        lines: lines.map(l => ({
          component_item_id: l.component_item_id,
          quantity: parseFloat(l.quantity) || 1,
          uom: l.uom || 'PCS',
          warehouse_id: l.warehouse_id || null,
          issue_method: l.issue_method || 'Backflush',
          comments: l.comments || null,
          price_list: l.price_list || null,
          wip_account: l.wip_account || null,
          route_sequence: l.route_sequence ? parseInt(l.route_sequence) : null,
          project_code: l.project_code || null,
          project_name: l.project_name || null,
        })),
      };

      if (isNew) {
        await api.post('/bom', payload);
        flash('Bill of Materials created successfully');
      } else {
        await api.put(`/bom/${bomId}`, payload);
        flash('Bill of Materials updated successfully');
      }
      setDirty(false);
      onBack();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleExportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      const componentRows = lines.map((l, i) => {
        const qty = parseFloat(l.quantity) || 0;
        const price = parseFloat(l.unit_price) || 0;
        return {
          '#': i + 1,
          'Type': l.comp_type || 'Goods',
          'Component Item No.': l.item_no || '',
          'Description': l.component_desc || '',
          'Quantity': qty,
          'UoM': l.uom || 'PCS',
          'Warehouse': l.wh_code || (warehouses.find((w) => w.warehouse_id === Number(l.warehouse_id))?.wh_code || ''),
          'Issue Method': l.issue_method || 'Backflush',
          'Project Code': l.project_code || '',
          'Project Name': l.project_name || '',
          'Unit Price': price,
          'Total Production Std Cost': qty * price,
          'Price List': l.price_list || '',
          'Comments': l.comments || '',
          'WIP Account': l.wip_account || '',
          'Route Seq.': l.route_sequence || '',
        };
      });

      const wsComponents = XLSX.utils.json_to_sheet(componentRows);

      if (componentRows.length > 0) {
        const cols = Object.keys(componentRows[0]).map((key) => {
          let maxLen = key.length;
          componentRows.forEach((r) => {
            const val = r[key] != null ? String(r[key]) : '';
            if (val.length > maxLen) maxLen = val.length;
          });
          return { wch: Math.min(Math.max(maxLen + 3, 10), 50) };
        });
        wsComponents['!cols'] = cols;
      }

      const rawSheetName = (productNo || 'BOM_Detail').substring(0, 31).replace(/[\\/?*\[\]]/g, '_');
      XLSX.utils.book_append_sheet(wb, wsComponents, rawSheetName);

      const fileName = `BOM_${(productNo || 'Export').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
      flash('BOM exported to Excel successfully');
    } catch (err) {
      setError('Failed to export BOM to Excel: ' + (err.message || err));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete this Bill of Materials for "${productNo}"?`)) return;
    try {
      await api.del(`/bom/${bomId}`);
      flash('BOM deleted');
      onBack();
    } catch (e) { setError(e.message); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-sap-blue border-t-transparent rounded-full animate-spin" />
        <span className="ml-2 text-xs text-sap-textLight">Loading BOM…</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Title bar */}
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3.5 flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="text-xs text-sap-blue hover:underline font-semibold">← Bill of Materials</button>
        <span className="text-sap-border">|</span>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sap-blue to-sap-headerTo flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
              <path d="M9 14l2 2 4-4" />
            </svg>
          </div>
          <div>
            <p className="text-2xs text-sap-textLight uppercase tracking-wider font-semibold">Bill of Materials</p>
            <p className="text-sm font-bold text-sap-text">{productNo || 'New BOM'}</p>
          </div>
        </div>
        {/* Breadcrumb for multi-level BOM navigation */}
        {bomStack.length > 0 && (
          <div className="flex items-center gap-1 text-xs">
            <span className="text-sap-border">|</span>
            <button onClick={() => navigateBreadcrumb(0)}
              className="text-sap-blue hover:underline font-medium">Root BOM</button>
            {bomStack.slice(1).map((entry, i) => (
              <React.Fragment key={i + 1}>
                <span className="text-sap-textLight">›</span>
                <button onClick={() => navigateBreadcrumb(i + 1)}
                  className="text-sap-blue hover:underline font-medium">{entry.product_no}</button>
              </React.Fragment>
            ))}
            <span className="text-sap-textLight">›</span>
            <span className="font-semibold text-sap-text">{productNo}</span>
          </div>
        )}
        {dirty && <span className="text-2xs text-amber-600 font-semibold">Unsaved</span>}
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleExportExcel}
          disabled={lines.length === 0}
          className="px-3 py-1.5 text-xs border border-emerald-600 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg font-semibold transition-colors disabled:opacity-40 flex items-center gap-1.5"
          title="Export BOM to Excel (.xlsx)"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Export Excel
        </button>
        {!isNew && (
          <button onClick={handleDelete}
            className="px-3 py-1.5 text-xs border border-red-300 text-red-500 rounded-lg hover:bg-red-50 font-semibold transition-colors">
            Delete
          </button>
        )}
        <button onClick={onBack}
          className="px-3 py-1.5 text-xs border border-sap-border rounded-lg text-sap-textLight hover:bg-gray-50 font-semibold">
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving}
          className="btn-primary disabled:opacity-60">
          {saving ? 'Saving…' : isNew ? 'Add' : 'Update'}
        </button>
      </div>

      {error && <Banner type="error">{error}</Banner>}

      {/* Header fields – SAP B1 style two-column layout */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {/* Left column */}
          <div className="space-y-3">
            <Field label="Product No.">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  {isNew ? (
                    <PaginatedItemPicker
                      value={form.product_item_id}
                      valueLabel={productNo}
                      onChange={(v) => void selectProduct(v)}
                      fetchPage={(opts) => fetchItemsPage({ ...opts, makeOnly: false })}
                      fetchMatchingIds={(opts) => fetchMatchingItemIds({ ...opts, makeOnly: false })}
                      placeholder="Select…"
                    />
                  ) : (
                    <input className={INP} value={productNo} disabled />
                  )}
                </div>
                <span className="text-xs text-sap-textLight whitespace-nowrap">X Quantity</span>
                <input type="number" min="1" step="1" className={`${INP} flex-1`}
                  value={form.quantity} onChange={e => set('quantity', e.target.value)} />
              </div>
            </Field>
            <Field label="Product Description">
              <input className={INP} value={productDesc} disabled />
            </Field>
            <Field label="BOM Type">
              <select className={INP} value={form.bom_type} onChange={e => set('bom_type', e.target.value)}>
                {BOM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Production Std Cost">
              <input className={`${INP} bg-gray-50`} value={`${prodStdCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} INR`} disabled />
            </Field>
            <Field label="Planned Average Production Size">
              <input type="number" min="1" step="0.01" className={INP}
                value={form.planned_avg_production_size} onChange={e => set('planned_avg_production_size', e.target.value)} />
            </Field>
          </div>

          {/* Right column */}
          <div className="space-y-3">
            <Field label="From Warehouse">
              <select className={INP} value={form.warehouse_id}
                onChange={e => set('warehouse_id', e.target.value)}>
                <option value="">—</option>
                {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.wh_code} – {w.wh_name}</option>)}
              </select>
            </Field>
            <Field label="To Warehouse">
              <select className={INP} value={form.to_warehouse_id}
                onChange={e => set('to_warehouse_id', e.target.value)}>
                <option value="">—</option>
                {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.wh_code} – {w.wh_name}</option>)}
              </select>
            </Field>
            <Field label="Price List">
              <input className={INP} value={form.price_list}
                onChange={e => set('price_list', e.target.value)} placeholder="" />
            </Field>
            <Field label="Distr. Rule">
              <input className={INP} value={form.distr_rule}
                onChange={e => set('distr_rule', e.target.value)} placeholder="" />
            </Field>
            <Field label="Project">
              <SearchableSelect
                value={form.project}
                onChange={v => set('project', v)}
                options={[
                  { value: '', label: '—' },
                  ...projects
                    .filter(p => p && p.project_code)
                    .map(p => ({ value: p.project_code, label: `${p.project_code} — ${p.project_name || ''}` })),
                ]}
                placeholder="Select project…"
              />
            </Field>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="flex border-b border-sap-border/40">
          {['Contents', 'Attachments'].map(t => (
            <button key={t} onClick={() => setTab(t.toLowerCase())}
              className={`px-5 py-2.5 text-xs font-semibold transition-colors border-b-2
                ${tab === t.toLowerCase()
                  ? 'border-sap-blue text-sap-blue bg-sap-blue/5'
                  : 'border-transparent text-sap-textLight hover:text-sap-text'}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === 'contents' && (
          <>
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-sap-border/40">
              <span className="text-xs font-semibold text-sap-text">Components ({lines.length})</span>
              <button onClick={addLine}
                className="px-2.5 py-1 text-2xs bg-sap-blue/10 text-sap-blue rounded-lg hover:bg-sap-blue/20 font-bold transition-colors">
                + Add Row
              </button>
            </div>

            {/* Grid */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-sap-border">
                    {['#', 'Type', 'No.', 'Description', 'Quantity', 'UoM Name', 'Warehouse',
                      'Issue Method', 'Project Code', 'Project Name',
                      'Production Std Cost', 'Total Production Std Cost',
                      'Price List', 'Unit Price', 'Total', 'Comments', 'WIP Account', 'Route Seq.', ''].map(h => (
                      <th key={h} className="text-left px-2 py-2.5 font-semibold text-sap-textMuted text-2xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr><td colSpan={19} className="py-8 text-center text-sap-textLight">
                      No components. Click "+ Add Row" to add materials.
                    </td></tr>
                  ) : (
                    linesOnPage.map((line, j) => {
                    const idx = compRowStart + j;
                    const isRouteStage = (line.comp_type === 'Route Stage' || line.line_type === 'Route Stage');
                    const isTextLine = (line.comp_type === 'Text' || line.line_type === 'Text');
                    const lineQty = parseFloat(line.quantity) || 0;
                    const lineUnitPrice = parseFloat(line.unit_price) || 0;
                    const lineTotal = lineQty * lineUnitPrice;

                    const rowBg = isRouteStage
                      ? 'bg-slate-200/80 font-bold border-y border-slate-400 text-slate-900'
                      : isTextLine
                      ? 'bg-amber-50/70 text-amber-900 border-b border-amber-200'
                      : idx % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]';

                    return (
                      <tr key={`bom-line-${idx}-${line.component_item_id || 'e'}`} className={`${rowBg} border-b border-sap-border/40 transition-colors`}>
                        <td className="px-2 py-1 text-center text-sap-textLight w-8 font-mono">{idx + 1}</td>
                        <td className="px-1 py-1 min-w-[100px]">
                          <select
                            className="w-full h-7 px-1 text-xs border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue bg-white/90 font-medium"
                            value={line.comp_type || line.line_type || 'Item'}
                            onChange={e => updateLine(idx, 'comp_type', e.target.value)}
                          >
                            <option value="Item">Item</option>
                            <option value="Resource">Resource</option>
                            <option value="Route Stage">Route Stage</option>
                            <option value="Text">Text</option>
                          </select>
                        </td>
                        <td className="px-1 py-1 min-w-[140px]">
                          {line.item_no ? (
                            <div className="flex items-center gap-1">
                              <span className={`font-semibold text-xs px-1 ${isRouteStage ? 'text-slate-900 font-bold' : 'text-sap-blue'}`}>
                                {line.item_no}
                              </span>
                              {!isRouteStage && (
                                <button
                                  type="button"
                                  title={`Open Item Master for ${line.item_no}`}
                                  onClick={(e) => { e.stopPropagation(); navigate(`/item-master/${encodeURIComponent(line.item_no)}`); }}
                                  className="flex-shrink-0 flex items-center justify-center w-5 h-5 hover:opacity-80 transition-opacity"
                                >
                                  <svg width="12" height="12" viewBox="0 0 14 14" className="drop-shadow-sm">
                                    <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
                                  </svg>
                                </button>
                              )}
                              {componentBomMap[line.component_item_id] && drillIntoBom && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); drillIntoBom(componentBomMap[line.component_item_id], line.item_no); }}
                                  title={`View BOM for ${line.item_no}`}
                                  className="p-0.5 rounded hover:bg-amber-100 transition-colors shrink-0">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#D4A017" stroke="#B8860B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 18l6-6-6-6"/>
                                  </svg>
                                </button>
                              )}
                            </div>
                          ) : (
                            <PaginatedItemPicker
                              multiple
                              value=""
                              onBulkAdd={(ids) => void bulkAddComponentsAt(idx, ids)}
                              fetchPage={(opts) => fetchItemsPage({ ...opts, makeOnly: false })}
                              fetchMatchingIds={(opts) => fetchMatchingItemIds({ ...opts, makeOnly: false })}
                              placeholder="Select…"
                              className="min-w-[130px]"
                            />
                          )}
                        </td>
                        <td className={`px-2 py-1 truncate max-w-[200px] min-w-[150px] ${isRouteStage ? 'font-bold text-slate-900' : 'text-sap-textLight'}`}>
                          {line.component_desc}
                        </td>
                        <td className="px-1 py-1 min-w-[80px]">
                          {isRouteStage ? (
                            <span className="text-xs text-slate-400 block text-right font-mono">—</span>
                          ) : (
                            <input type="number" min="0" step="0.0001"
                              className="w-full h-7 px-1.5 text-xs text-right border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue bg-white"
                              value={line.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} />
                          )}
                        </td>
                        <td className="px-2 py-1 min-w-[70px] text-xs">{isRouteStage ? '—' : (line.uom || 'PCS')}</td>
                        <td className="px-1 py-1 min-w-[100px]">
                          {isRouteStage ? (
                            <span className="text-xs text-slate-400 block text-center">—</span>
                          ) : (
                            <select className="w-full h-7 px-1 text-xs border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue bg-white"
                              value={line.warehouse_id || ''} onChange={e => updateLine(idx, 'warehouse_id', e.target.value)}>
                              <option value="">—</option>
                              {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.wh_code}</option>)}
                            </select>
                          )}
                        </td>
                        <td className="px-1 py-1 min-w-[100px]">
                          {isRouteStage ? (
                            <span className="text-xs text-slate-400 block text-center">—</span>
                          ) : (
                            <select className="w-full h-7 px-1 text-xs border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue bg-white"
                              value={line.issue_method} onChange={e => updateLine(idx, 'issue_method', e.target.value)}>
                              {ISSUE_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                          )}
                        </td>
                        <td className="px-2 py-1 text-xs min-w-[110px] text-sap-text">{line.project_code || '—'}</td>
                        <td className="px-2 py-1 text-xs min-w-[130px] text-sap-textLight truncate max-w-[180px]">{line.project_name || '—'}</td>
                        <td className="px-2 py-1 text-right text-xs min-w-[110px]">
                          {!isRouteStage && lineUnitPrice > 0 ? lineUnitPrice.toLocaleString('en-IN', { minimumFractionDigits: 3 }) : '—'}
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-xs min-w-[130px]">
                          {!isRouteStage && lineTotal > 0 ? lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 3 }) : '—'}
                        </td>
                        <td className="px-1 py-1 min-w-[90px]">
                          <input className="w-full h-7 px-1.5 text-xs border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue bg-white"
                            value={line.price_list || ''} onChange={e => updateLine(idx, 'price_list', e.target.value)}
                            placeholder="" />
                        </td>
                        <td className="px-2 py-1 text-right text-xs min-w-[80px]">
                          {!isRouteStage && lineUnitPrice > 0 ? lineUnitPrice.toLocaleString('en-IN', { minimumFractionDigits: 4 }) : '—'}
                        </td>
                        <td className="px-2 py-1 text-right font-semibold text-xs min-w-[80px]">
                          {!isRouteStage && lineTotal > 0 ? lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 4 }) : '—'}
                        </td>
                        <td className="px-1 py-1 min-w-[120px]">
                          <input className="w-full h-7 px-1.5 text-xs border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue bg-white"
                            value={line.comments || ''} onChange={e => updateLine(idx, 'comments', e.target.value)}
                            placeholder="" />
                        </td>
                        <td className="px-1 py-1 min-w-[100px]">
                          <input className="w-full h-7 px-1.5 text-xs border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue bg-white"
                            value={line.wip_account || ''} onChange={e => updateLine(idx, 'wip_account', e.target.value)}
                            placeholder="" />
                        </td>
                        <td className="px-1 py-1 min-w-[70px]">
                          <input type="number" min="0" step="1"
                            className="w-full h-7 px-1.5 text-xs text-right border border-sap-border/60 rounded focus:outline-none focus:border-sap-blue font-mono font-semibold bg-white"
                            value={line.route_sequence || ''} onChange={e => updateLine(idx, 'route_sequence', e.target.value)}
                            placeholder="1" />
                        </td>
                        <td className="px-1 py-1 text-center w-8">
                          <button onClick={() => removeLine(idx)} className="text-red-400 hover:text-red-600 text-xs font-bold" title="Remove">✕</button>
                        </td>
                      </tr>
                    );
                    })
                  )}
                  {/* Totals row */}
                  {lines.length > 0 && (
                    <tr className="bg-sap-blue/5 font-bold border-t-2 border-sap-blue/30">
                      <td colSpan={10} className="px-3 py-2 text-sap-text">Total</td>
                      <td className="px-2 py-2 text-right text-sap-blue text-xs">
                        {lines.reduce((s, l) => s + (parseFloat(l.unit_price) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 3 })}
                      </td>
                      <td className="px-2 py-2 text-right text-sap-blue text-xs">
                        {prodStdCost.toLocaleString('en-IN', { minimumFractionDigits: 3 })}
                      </td>
                      <td /> {/* Price List */}
                      <td className="px-2 py-2 text-right text-sap-blue text-xs">
                        {lines.reduce((s, l) => s + (parseFloat(l.unit_price) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                      </td>
                      <td className="px-2 py-2 text-right text-sap-blue text-xs">
                        {prodStdCost.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                      </td>
                      <td colSpan={4} />
                    </tr>
                  )}
                </tbody>
              </table>
              {lines.length > COMPONENTS_TABLE_PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-2 border-t border-sap-border/40 bg-[#fafbfb] text-2xs text-sap-text">
                  <span>
                    Showing {compRowStart + 1}–{compRowStart + linesOnPage.length} of {lines.length} rows
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={compPageSafe <= 0}
                      onClick={() => setCompTablePage((p) => Math.max(0, p - 1))}
                      className="px-2 py-1 rounded border border-sap-border disabled:opacity-40 hover:bg-white"
                    >
                      Prev
                    </button>
                    <span className="text-sap-textLight">
                      Page {compPageSafe + 1} / {compTotalPages}
                    </span>
                    <button
                      type="button"
                      disabled={compPageSafe >= compTotalPages - 1}
                      onClick={() => setCompTablePage((p) => Math.min(compTotalPages - 1, p + 1))}
                      className="px-2 py-1 rounded border border-sap-border disabled:opacity-40 hover:bg-white"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {tab === 'attachments' && (
          <div className="flex">
            {/* Table area */}
            <div className="flex-1 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-sap-border">
                    {['#', 'Target Path', 'File Name', 'File Extension', 'File Size',
                      'Attachment Date', 'Attached By', 'Free Text', 'Copy to Doc.'].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 font-semibold text-sap-textMuted text-2xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(!attachments || attachments.length === 0) ? (
                    <tr><td colSpan={9} className="py-16 text-center text-sap-textLight">No attachments.</td></tr>
                  ) : attachments.map((a, i) => (
                    <tr key={a.attachment_id || i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]'} border-b border-sap-border/40`}>
                      <td className="px-3 py-1.5 text-sap-textLight text-center w-8">{i + 1}</td>
                      <td className="px-3 py-1.5 text-sap-textLight">{a.target_path || '—'}</td>
                      <td className="px-3 py-1.5 font-medium">{a.file_name || '—'}</td>
                      <td className="px-3 py-1.5">{a.file_extension || (a.file_name?.split('.').pop()) || '—'}</td>
                      <td className="px-3 py-1.5">{a.file_size ? `${(a.file_size / 1024).toFixed(1)} KB` : '—'}</td>
                      <td className="px-3 py-1.5">{a.attachment_date?.slice(0, 10) || a.uploaded_at?.slice(0, 10) || '—'}</td>
                      <td className="px-3 py-1.5">{a.attached_by || '—'}</td>
                      <td className="px-3 py-1.5 text-sap-textLight">{a.free_text || ''}</td>
                      <td className="px-3 py-1.5 text-center">{a.copy_to_target ? '✓' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Right-side buttons */}
            <div className="flex flex-col gap-2 px-4 py-3 border-l border-sap-border/40 min-w-[90px]">
              <button className="px-3 py-1.5 text-xs border border-sap-border rounded hover:bg-gray-50 transition-colors text-sap-text font-medium">
                Browse
              </button>
              <button className="px-3 py-1.5 text-xs border border-sap-border rounded hover:bg-gray-50 transition-colors text-sap-text font-medium">
                Display
              </button>
              <div className="flex-1" />
              <button className="px-3 py-1.5 text-xs border border-red-200 text-red-500 rounded hover:bg-red-50 transition-colors font-medium">
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/* Shared helpers */
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-2xs font-semibold text-sap-textLight uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}

function Banner({ type, children }) {
  const cls = type === 'error'
    ? 'bg-red-50 border-red-200 text-red-600'
    : 'bg-green-50 border-green-200 text-green-700';
  return <div className={`px-3 py-2 rounded-lg border text-xs ${cls}`}>{children}</div>;
}